import {
  InformalFeedbackRequest,
  InformalFeedbackRequestState,
  InformalFeedbackReviewState,
} from 'api/clients/InformalFeedbackRequestsClient'
import Styles from './RequesterCard.module.scss'
import Avatar from 'common/Avatar/Avatar'
import { Text } from 'blaze-ui/Text/Text'
import { getInitials } from 'utils'
import { useAuth } from 'hooks/UseAuth'
import dayjs from 'dayjs'
import { dateFormat } from 'utils/dates'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import { RequestCard } from '../RequestCard'
import { Button } from 'blaze-ui/Button'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useModals } from 'almanac-ui/Modal'
import { getPendingReReviews } from 'utils/informalFeedbackRequest'
import { last } from 'lodash'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ReviewSubmissionDrawer } from 'components/DocRightPanelWrapper/Panels/ReviewSubmissionPanel/ReviewSubmissionDrawer'
import useResponsiveFunction from 'hooks/useResponsiveFunction'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'

interface Props {
  request: InformalFeedbackRequest
}

export const RequesterCard = observer(({ request }: Props) => {
  const { rightRailPanelStore, drawersStore } = useRootStore()
  const { closeAllModals } = useModals()
  const requester = request.requester
  const auth = useAuth()
  const { isMobile } = useResponsiveDesign()
  const requesterIsMe = auth && requester.id === auth.id
  const creationDate = dayjs(request.created_at).format(`${dateFormat(request.created_at)} h:mm a`)
  const reviewToSubmit = request?.reviews?.filter(
    ({ reviewer, dismissed_reason, state }) =>
      reviewer.id === auth?.id && !dismissed_reason && state === InformalFeedbackReviewState.Pending,
  )[0]

  const handleFinishReview = useResponsiveFunction({
    mobileFunction: () => drawersStore.openDrawer(ReviewSubmissionDrawer, { request }),
    defaultFunction: () => {
      rightRailPanelStore.show('review_submission')
      closeAllModals()
    },
  })

  const reRequest = last(getPendingReReviews(request))
  const message = reRequest?.informal_feedback_request_message || request.html_description || request.description

  return (
    <RequestCard.Root role='requester'>
      <div className={Styles.header}>
        <div className={Styles.requester}>
          <Avatar url={requester.avatar_url} label={getInitials(requester.display_name)} size={25} />
          {reRequest ? (
            <>
              <Text color="var(--dark-80)" variant="secondary">
                <div className={Styles.reRequested}>
                  <b>{requesterIsMe ? 'You' : requester.display_name}</b> asked for another review because it was sent
                  back by {reRequest.reviewer.display_name}
                  <ResponsiveDesign.Desktop>
                    <span className={Styles.creationDate}>{creationDate}</span>
                  </ResponsiveDesign.Desktop>
                </div>
              </Text>
            </>
          ) : (
            <Text color="var(--dark-80)" variant="secondary">
              <span className={Styles.requesterName}>{requesterIsMe ? 'You' : requester.display_name}</span> asked to
              review this file
            </Text>
          )}
        </div>
        <ResponsiveDesign.Desktop>
          {!reRequest && <div className={Styles.creationDate}>{creationDate}</div>}
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <span className={Styles.creationDate}>{creationDate}</span>
        </ResponsiveDesign.Mobile>
      </div>

      {message && (
        <RequestCard.Message>
          <ProseDisplay html={message} />
        </RequestCard.Message>
      )}

      {!requesterIsMe && !!reviewToSubmit && request.state === InformalFeedbackRequestState.Open && (
        <div>
          <Button fullWidth={isMobile} onPress={handleFinishReview}>
            Finish Your Review
          </Button>
        </div>
      )}
    </RequestCard.Root>
  )
})
